import type { DropdownOption } from 'components/DropdownBuilder';
import { sampleRandomColorFromPalette } from 'constants/colorPalette';
import { BUDGET_CALCULATION, BUDGET_INTERVAL, BUDGET_TYPES } from 'constants/project';
import type { TagNormalized, TagsDictionary } from 'redux/modules/tags.types';
import type { Client } from 'types/client';
import type {
  EnrichedProject,
  EnrichedProjectWithBudget,
  FilterGroupValues,
  ProjectBase,
  ProjectBudgetAttributes,
  ProjectLabel,
  ProjectUserRelation,
} from 'types/project';
import type { LightUser } from 'types/user';
import { naturalCompare } from 'utils/naturalSort';
import {
  calculateBudgetPercent,
  calculateBudgetProgress,
  calculateBudgetRemaining,
  getProjectColor,
} from 'utils/project';
import { notNull } from 'utils/typesafe';
/**
 * Return the minimum required project payload for things like HourDraft
 * Usefor for embedding basic references to a projects -- supports LIGHT projects
 */
export const makeEmptyProjectBase = (name?: string): ProjectBase => {
  return {
    id: null as any,
    active: false,
    name: name ?? '',
    color: 'cccccc',
    client: {
      id: null as any,
      name: '',
      color: sampleRandomColorFromPalette(),
    },
  };
};

export const makeEmptyEnrichedProject = (name?: string): EnrichedProject => {
  return {
    base: makeEmptyProjectBase(name),
    isFullyLoaded: false,
    fullProject: null,
  };
};

export const enrichProjectWithBudgetAttributes = (
  project: EnrichedProject
): EnrichedProjectWithBudget => {
  const budgetProgress = project.fullProject ? calculateBudgetProgress(project.fullProject) : 0;
  const budgetRemaining = project.fullProject ? calculateBudgetRemaining(project.fullProject) : 0;
  const budgetSpendInPercent = project.fullProject
    ? calculateBudgetPercent(project.fullProject)
    : 0;
  const budgetRecurrence = project.fullProject?.budget_recurrence;
  const hasBudgetExpired = !!project.fullProject?.budget_expired_on;
  const hasRecurringBudget =
    budgetRecurrence != null && budgetRecurrence.recur !== BUDGET_INTERVAL.NONE;
  const hasBudgetStarted =
    !hasRecurringBudget ||
    (hasRecurringBudget &&
      !!budgetRecurrence.start_date &&
      new Date(budgetRecurrence.start_date) <= new Date());
  const budgetAttributes: ProjectBudgetAttributes = {
    hasBudget:
      project.fullProject?.budget_type !== BUDGET_TYPES.NONE &&
      !!project.fullProject?.budget_type &&
      project.fullProject?.budget !== 0 &&
      !hasBudgetExpired &&
      hasBudgetStarted,
    hasRecurringBudget: hasRecurringBudget,
    budgetValue: project.fullProject?.budget ?? 0,
    budgetSpend: budgetProgress,
    budgetRemaining: budgetRemaining,
    budgetIsPending: project.fullProject?.budget_calculation === BUDGET_CALCULATION.PENDING,
    budgetIsMoney: project.fullProject?.budget_type === BUDGET_TYPES.MONEY,
    budgetIsHours: project.fullProject?.budget_type === BUDGET_TYPES.HOUR,
    budgetSpendInPercent: budgetSpendInPercent,
    budgetProgress: budgetProgress,
    loggedMinutes: project.fullProject?.duration?.total_minutes || 0,
    plannedMinutes: project.fullProject?.estimated_duration?.total_minutes || 0,
    loggedMoney: project.fullProject?.cost?.amount || 0,
    plannedMoney: project.fullProject?.estimated_cost?.amount || 0,
    billedMoney: project.fullProject?.billed_cost?.amount || 0,
    billedMinutes: project.fullProject?.billed_duration?.total_minutes || 0,
    unbilledMoney: project.fullProject?.unbilled_cost?.amount || 0,
    unbilledMinutes: project.fullProject?.unbilled_duration?.total_minutes || 0,
  };
  return {
    ...project,
    budgetAttributes,
  };
};

export interface ProjectDropdownOption extends DropdownOption<number> {
  forecastId?: number;
  color?: string;
  billable?: boolean;
  client?: string | null;
  showClient?: boolean;
  clientId?: number | null;
  totalProjects?: number;
  budgetProgress?: number | null;
  budgetSpendInPercent?: number | null;
  budgetIsPending?: boolean;
  project: ProjectBase | null | undefined;
  createTrigger?: boolean;
  loadMoreTrigger?: boolean;
  createValue?: string | null | undefined;
  isArchived?: boolean;
  isPinned?: boolean;
  currencySymbol?: string;
}

export const clientWithProjectsAsDropdownOptions = (
  clientWithProjects: Record<
    string,
    {
      client: Client;
      projects: Array<EnrichedProject>;
    }
  > = {},
  params: {
    currencySymbol: string;
    hideArchivedProjects: boolean;
    projectIdWhitelist: ReadonlyArray<number> | null;
    filterGroup: FilterGroupValues;
  } = {
    currencySymbol: '$',
    projectIdWhitelist: null,
    hideArchivedProjects: false,
    filterGroup: 'all',
  }
): Array<ProjectDropdownOption> => {
  const { currencySymbol, projectIdWhitelist, hideArchivedProjects, filterGroup } = params;
  return Object.keys(clientWithProjects)
    .map((clientKey): ProjectDropdownOption | null => {
      const group = clientWithProjects[clientKey];
      const options: Array<ProjectDropdownOption> = group.projects
        .map((project): ProjectDropdownOption => {
          return {
            value: project.base.id,
            label: project.base.name,
            color: getProjectColor(project.base),
            billable: project.fullProject?.billable,
            client: group.client.name,
            clientId: group.client.id,
            // We don't want to show progressbar for now
            budgetProgress: null, // project.budget ? getBudgetProgress(project) : null,
            isArchived: !project.base.active,
            isPinned: project.isPinned,
            currencySymbol,
            project: project.fullProject,
          };
        })
        .filter(
          project =>
            filterGroup === 'all' ||
            project.isArchived === (filterGroup === 'archived') ||
            !project.isArchived === (filterGroup === 'active')
        )
        .sort((a, b) => {
          return naturalCompare(a.label, b.label);
        });

      const filteredOptions =
        projectIdWhitelist || hideArchivedProjects
          ? options.filter(
              ({ value, isArchived }) =>
                !(hideArchivedProjects && isArchived) &&
                (projectIdWhitelist?.includes(value) ?? true)
            )
          : options;

      if (filteredOptions.length === 0) {
        return null;
      }

      return {
        label: group.client.name,
        clientId: group.client.id,
        rowKey: `client-${group.client.id}`,
        children: filteredOptions,
        value: -1,
        project: null,
      };
    })
    .filter(notNull)
    .sort((a, b) => naturalCompare(a.label, b.label));
};

// TODO any
export function tagListFactory(label_id: number, oldState: any = {}, default_ = false) {
  return {
    label_id,
    required: false,
    default: default_,
    ...oldState,
  };
}

// TODO any
export function tagFactory(label_id: number, oldState: any = {}, default_ = false) {
  return {
    label_id,
    budget: 0,
    default: default_,
    ...oldState,
  };
}

export const normalizeProject = (
  project: EnrichedProject,
  includeCurrency = true
): ProjectDropdownOption => ({
  value: project.base.id,
  label: project.base.name,
  color: getProjectColor(project.base),
  billable: project.fullProject?.billable,
  client: project.base.client.name,
  clientId: project.base.client.id,
  budgetProgress: project.fullProject?.budget ? calculateBudgetProgress(project.fullProject) : null,
  budgetSpendInPercent: project.fullProject ? calculateBudgetPercent(project.fullProject) : 0,
  budgetIsPending: project.fullProject?.budget_calculation === BUDGET_CALCULATION.PENDING,
  isArchived: !project.base.active,
  isPinned: project.isPinned,
  project: project.base,
  currencySymbol: includeCurrency ? project.fullProject?.currency?.symbol : undefined,
});

interface CreateClientProjectTreeOptions {
  currencySymbol?: string;
  onlyActive?: boolean;
  addLoadMoreTriggers?: boolean;
  filterByTagIds?: Array<number>;
  projectIdsByTagId?: Record<number, Array<number>>;
}

export function createClientProjectTree(
  projects: Array<EnrichedProject>,
  clients: Array<Client>,
  {
    onlyActive = true,
    addLoadMoreTriggers,
    filterByTagIds,
    projectIdsByTagId,
  }: CreateClientProjectTreeOptions
): Array<ProjectDropdownOption> {
  const sortedClients = [...clients];
  sortedClients.sort((a, b) => naturalCompare(a.name, b.name));
  // Build client->project association before loop to avoid O(n*m) performance
  let multiCurrency: string | undefined | true;
  const projectsByClientId = projects.reduce(
    (map: Record<number, Array<EnrichedProject>>, project) => {
      if (!multiCurrency) {
        multiCurrency = project.fullProject?.currency.id;
      } else if (
        multiCurrency !== true &&
        !!project.fullProject?.currency.id &&
        multiCurrency !== project.fullProject?.currency.id
      ) {
        multiCurrency = true;
      }

      if (onlyActive && !project.base.active) {
        return map;
      }
      if (map[project.base.client.id] == null) {
        map[project.base.client.id] = [];
      }
      map[project.base.client.id].push(project);
      return map;
    },
    {}
  );

  const options: Array<ProjectDropdownOption> = sortedClients
    .map((client): ProjectDropdownOption => {
      let children = (projectsByClientId[client.id] || [])
        .sort((a, b) => naturalCompare(a.base.name, b.base.name))
        .map(project => {
          // Ensure we use the client from the clients array for consistent color
          // instead of the potentially stale client data in project.base.client
          const projectWithUpdatedClient: EnrichedProject = {
            ...project,
            base: {
              ...project.base,
              client: client,
            },
          };
          return normalizeProject(projectWithUpdatedClient, multiCurrency === true);
        });
      if (addLoadMoreTriggers && (client.active_projects_count ?? 0) > children.length) {
        const moreToLoad = (client.active_projects_count ?? 0) - children.length;
        children.push({
          label: `Load remaining ${moreToLoad} projects`,
          value: -1,
          rowKey: `loadmore-${client.id}`,
          project: null,
          loadMoreTrigger: true,
          clientId: client.id,
        });
      }

      // When filtering by tags, we need to do it after the projects have been counted so the user
      // doesn't see a "Load remaining projects" message
      if (filterByTagIds?.length && projectIdsByTagId) {
        children = children.filter(project =>
          filterByTagIds.some(id => projectIdsByTagId[id]?.includes(project.value))
        );
      }

      return {
        value: -1,
        rowKey: `client-${client.id}`,
        label: client.name,
        clientId: client.id,
        totalProjects: client.active_projects_count,
        children,
        project: null,
      };
    })
    .filter(option => !!option.children?.length);

  return options;
}

/**
 * Turn a User into project.users association
 */
export function userToProjectUserRelationFactory(user: LightUser): ProjectUserRelation {
  const hour_rate = user.default_hour_rate || 0;
  return {
    user_id: user.id,
    hour_rate,
  };
}

export function sortSelectedTagLists(
  lists: Record<number, ProjectLabel>,
  entities: TagsDictionary
) {
  return Object.keys(lists)
    .reduce((prev: Array<TagNormalized>, id: string) => {
      const lookupEntity = entities[+id];

      if (!lookupEntity || lookupEntity.parent_id) {
        return prev;
      }

      return prev.concat(lookupEntity);
    }, [])
    .sort((a, b) => naturalCompare(a.name, b.name));
}

export const incompleteOption: ProjectDropdownOption = {
  value: -1,
  rowKey: 'incomplete-footer',
  project: null,
  label: 'This list may be incomplete; please search to see more',
  isSelectable: false,
};
